import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import React from "react";

import { useNetworkDetails } from "@features/ax/assetRegistry/networks/hooks";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { AlertTriangle } from "lucide-react";
import { useParams } from "react-router-dom";

interface Asset {
  id: number;
  name: string;
  remainingLife: string;
  currentBookValue: number;
  replacementCost: number;
  urgency: string;
}

interface NetworkFinancialData {
  annualDepreciation: number;
  depreciationToDate: number;
  remainingDepreciation: number;
  nextDepreciationDate: string;
}

export const NetworkFinanceOverview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const currency = useCurrency();
  const { data: financeData } = useNetworkDetails({
    tab: "finance/overview",
    networkId: id,
    remote_utility_id: getRemoteUtilityId(),
  });

  // Extract data from hook response
  const networkFinancialData: NetworkFinancialData = {
    annualDepreciation:
      financeData?.result?.networkDepreciationSchedule?.annualDepreciation || 0,
    depreciationToDate:
      financeData?.result?.networkDepreciationSchedule?.depreciationToDate || 0,
    remainingDepreciation:
      financeData?.result?.networkDepreciationSchedule?.remainingDepreciation ||
      0,
    nextDepreciationDate:
      financeData?.result?.networkDepreciationSchedule?.nextDepreciationDate ||
      "N/A",
  };

  const endOfLifeColumns: TableColumn<any>[] = [
    {
      key: "assetName",
      header: "Asset Name",
      type: "text",
      render: (value, record) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-blue-600 hover:underline">
                {value}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-sm">
              This asset is nearing its end-of-life. Its replacement will
              require an estimated capital expenditure of{" "}
              <span className="font-semibold">
                {record.estimatedReplacementCost?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </span>
              . This information is critical for long-term capital planning.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      key: "remainingLife",
      header: "Remaining Life",
      type: "custom",
      render: (value: string, row: any) => <Badge>{value}</Badge>,
    },
    {
      key: "currentBookValue",
      header: "Current Book Value",
      type: "text",
      sortable: true,
    },
    {
      key: "replacementCost",
      header: "Est. Replacement Cost",
      type: "text",
      sortable: true,
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
  ];

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-green-100 text-green-800";
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Depreciation Schedule */}
        <Card>
          <CardHeader>
            <CardTitle>Network Depreciation Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-1">
                Annual Depreciation (Current Year):
                <CustomTooltip message="The amount of depreciation expense allocated for the current fiscal year." />
              </span>
              <span className="font-medium">
                {currency} {networkFinancialData.annualDepreciation}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-1">
                Depreciation To Date (Current Year):
                <CustomTooltip message="The portion of this year's annual depreciation that has already been recorded." />
              </span>
              <span className="font-medium">
                {currency} {networkFinancialData.depreciationToDate}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-1">
                Remaining Depreciation:
                <CustomTooltip message="The total amount of depreciation that has yet to be expensed over the network's remaining useful life." />
              </span>
              <span className="font-medium">
                {currency} {networkFinancialData.remainingDepreciation}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 flex items-center gap-1">
                Next Depreciation Date:
                <CustomTooltip message="The date the next annual depreciation expense will be officially recorded." />
              </span>
              <span className="font-medium">
                {networkFinancialData.nextDepreciationDate}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Components Nearing End-of-Life */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <div>
                <CardTitle>Assets Nearing End-of-Life</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Assets with less than 5 years of useful life remaining
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <GenericTable
              data={financeData?.result?.assetsNearingEndOfLife ?? []}
              columns={endOfLifeColumns}
              rowKey="assetId"
              emptyMessage="No assets nearing end-of-life"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
